/**
 * Screen height and on-screen keyboard, for every phone the office boys use.
 *
 * - `--app-h` on <html> is the height actually visible (above the keyboard when it is open),
 *   so full-screen layouts use `h-app` / `min-h-app` instead of `100dvh`, which older Android
 *   browsers (Chrome < 108) don't understand.
 * - `kb-open` on <html> while the keyboard covers part of a phone screen: the add screen then
 *   hides the slip (and its own keypad) so the field being typed in stays in view.
 * - The focused field is scrolled into view once the keyboard has opened.
 */

/** A drop this large (CSS px), with a text field focused, means the keyboard is up. */
export const KEYBOARD_MIN_PX = 120;
/** Phones only: tablets have room for the layout as it is. */
export const KEYBOARD_MAX_WIDTH = 768;

export function isEditable(el: Element | null): boolean {
  if (!el) return false;
  if (el instanceof HTMLTextAreaElement) return !el.readOnly;
  if (el instanceof HTMLInputElement) {
    const nonText = ['button', 'checkbox', 'radio', 'range', 'file', 'submit', 'reset', 'color', 'image', 'hidden'];
    return !el.readOnly && !nonText.includes(el.type) && el.inputMode !== 'none';
  }
  return el instanceof HTMLElement && el.isContentEditable;
}

/** Keyboard up = a text field is focused on a phone and the visible height dropped well below
 * the tallest height seen at this width. */
export function keyboardOpen(opts: { tallest: number; visible: number; width: number; editing: boolean }): boolean {
  return opts.editing && opts.width < KEYBOARD_MAX_WIDTH && opts.tallest - opts.visible >= KEYBOARD_MIN_PX;
}

export function watchViewport(win: Window = window): () => void {
  const doc = win.document.documentElement;
  const vv = win.visualViewport;
  let tallest = 0;
  let lastWidth = 0;

  const update = () => {
    // vv.height shrinks when zoomed in; multiply back so pinch-zoom doesn't squash the layout.
    // Round down: at browser zoom (110%, 125%…) the height is fractional, and rounding up made
    // the page 1 px taller than the window, which shows a scrollbar.
    const visible = Math.floor(vv ? vv.height * vv.scale : win.innerHeight);
    const width = win.innerWidth;
    if (width !== lastWidth) {
      // Rotated or resized: start again (the keyboard is rarely open at this moment).
      lastWidth = width;
      tallest = 0;
    }
    tallest = Math.max(tallest, visible);
    doc.style.setProperty('--app-h', `${visible}px`);
    const editing = isEditable(win.document.activeElement);
    const open = keyboardOpen({ tallest, visible, width, editing });
    doc.classList.toggle('kb-open', open);
    if (open) revealFocused(win);
  };

  const onFocus = () => win.setTimeout(update, 50);
  update();
  vv?.addEventListener('resize', update);
  win.addEventListener('resize', update);
  win.addEventListener('orientationchange', update);
  win.document.addEventListener('focusin', onFocus);
  win.document.addEventListener('focusout', onFocus);
  return () => {
    vv?.removeEventListener('resize', update);
    win.removeEventListener('resize', update);
    win.removeEventListener('orientationchange', update);
    win.document.removeEventListener('focusin', onFocus);
    win.document.removeEventListener('focusout', onFocus);
  };
}

/** Keep the field being typed in visible above the keyboard (once the layout has settled). */
function revealFocused(win: Window) {
  const el = win.document.activeElement;
  if (!(el instanceof HTMLElement) || !isEditable(el)) return;
  win.requestAnimationFrame(() => {
    const r = el.getBoundingClientRect();
    const bottom = win.visualViewport ? win.visualViewport.height : win.innerHeight;
    if (r.top < 0 || r.bottom > bottom) el.scrollIntoView({ block: 'center', inline: 'nearest' });
  });
}
